import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Drawer,
  Stack,
  Box,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Avatar,
  Tooltip,
  useTheme,
  useMediaQuery,
} from 'omaha-design-system';
import {
  X as CloseIcon,
  CaretDown as ExpandMoreIcon,
} from '@phosphor-icons/react';

// Types for injury report data
export interface PlayerInjury {
  playerName: string;
  playerId?: string;
  avatar?: string;
  team: string;
  status: 'Out' | 'Doubtful' | 'Questionable' | 'Probable';
  reason: string;
  projectedMinutes?: number;
  /** Position code */
  position?: string;
  /** Short text shown next to the name when there are no projected minutes (e.g. "QB") */
  detail?: string;
}

export type ImpactLevel = 'high' | 'medium' | undefined;

export interface GameInjuryReport {
  gameId: string;
  startTime?: string; // ISO timestamp for sorting
  awayTeam: {
    name: string;
    abbr: string;
    logo: string;
  };
  homeTeam: {
    name: string;
    abbr: string;
    logo: string;
  };
  gameTime: string;
  hasHighImpact?: boolean;
  impactLevel?: ImpactLevel;
  injuries: PlayerInjury[];
  awayTeamSubmitted?: boolean;
  homeTeamSubmitted?: boolean;
}

export interface InjuryReportData {
  date: string;
  lastUpdated: string;
  games: GameInjuryReport[];
}

interface InjuryReportModalProps {
  open: boolean;
  onClose: () => void;
  data?: InjuryReportData;
  onRefresh?: () => void;
  isLoading?: boolean;
  /** Game ID to auto-expand when modal opens */
  defaultExpandedGameId?: string;
  /** League code used in the title (e.g. "NBA", "NFL") */
  league?: string;
}

const STATUS_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  Out: { bg: 'rgba(239, 83, 80, 0.16)', text: '#EF5350', dot: '#EF5350' },
  Doubtful: { bg: 'rgba(229, 115, 115, 0.16)', text: '#E57373', dot: '#E57373' },
  Questionable: { bg: 'rgba(255, 167, 38, 0.16)', text: '#FFA726', dot: '#FFA726' },
  Probable: { bg: 'rgba(102, 187, 106, 0.16)', text: '#66BB6A', dot: '#66BB6A' },
};

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const colors = STATUS_COLORS[status] || STATUS_COLORS.Out;
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        backgroundColor: colors.bg,
        color: colors.text,
        fontFamily: '"Instrument Sans", sans-serif',
        fontWeight: 600,
        fontSize: '11.11px',
        height: 24,
        borderRadius: '4px',
        flexShrink: 0,
      }}
    />
  );
};

const IMPACT_COLORS = {
  high: { bg: 'rgba(239, 83, 80, 0.16)', text: '#EF5350', border: 'rgba(239, 83, 80, 0.5)' },
  medium: { bg: 'rgba(255, 167, 38, 0.16)', text: '#FFA726', border: 'rgba(255, 167, 38, 0.5)' },
};

const ImpactBadge: React.FC<{ level: 'high' | 'medium' }> = ({ level }) => {
  const colors = IMPACT_COLORS[level];
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: 0.25,
        borderRadius: '4px',
        backgroundColor: colors.bg,
        border: `1px solid ${colors.border}`,
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Instrument Sans", sans-serif',
          fontSize: '10px',
          fontWeight: 600,
          color: colors.text,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        {level === 'high' ? 'High Impact' : 'Medium Impact'}
      </Typography>
    </Box>
  );
};

const StatusFilterChip: React.FC<{
  status: string;
  count: number;
  dotColor: string;
}> = ({ status, count, dotColor }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 1.5,
      py: 0.75,
      borderRadius: '8px',
      border: '1px solid #616161',
      backgroundColor: '#2f3031',
    }}
  >
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: dotColor,
        flexShrink: 0,
      }}
    />
    <Typography
      sx={{
        fontFamily: '"Instrument Sans", sans-serif',
        fontSize: '11.11px',
        fontWeight: 500,
        color: '#F2F2F3',
        whiteSpace: 'nowrap',
      }}
    >
      {status} ({count})
    </Typography>
  </Box>
);

// Desktop player row - no avatar, just dot indicator
const PlayerInjuryRowDesktop: React.FC<{ injury: PlayerInjury }> = ({ injury }) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ py: 1 }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: STATUS_COLORS[injury.status]?.dot || '#EF5350',
            flexShrink: 0,
          }}
        />
        <Stack spacing={0}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              sx={{
                fontFamily: '"Instrument Sans", sans-serif',
                fontSize: '13.33px',
                fontWeight: 600,
                color: '#F2F2F3',
                lineHeight: 1.5,
              }}
            >
              {injury.playerName}
            </Typography>
            {injury.projectedMinutes !== undefined && (
              <Typography
                sx={{
                  fontFamily: '"Instrument Sans", sans-serif',
                  fontSize: '11px',
                  fontWeight: 500,
                  color: 'rgba(242, 242, 243, 0.5)',
                  lineHeight: 1.5,
                }}
              >
                ({Math.round(injury.projectedMinutes)} min)
              </Typography>
            )}
            {injury.projectedMinutes === undefined && injury.detail && (
              <Typography
                sx={{
                  fontFamily: '"Instrument Sans", sans-serif',
                  fontSize: '11px',
                  fontWeight: 500,
                  color: 'rgba(242, 242, 243, 0.5)',
                  lineHeight: 1.5,
                }}
              >
                ({injury.detail})
              </Typography>
            )}
          </Stack>
          <Typography
            sx={{
              fontFamily: '"Instrument Sans", sans-serif',
              fontSize: '13.33px',
              fontWeight: 400,
              color: 'rgba(242, 242, 243, 0.38)',
              lineHeight: 1.5,
            }}
          >
            {injury.reason}
          </Typography>
        </Stack>
      </Stack>
      <StatusChip status={injury.status} />
    </Stack>
  );
};

// Mobile player row - with avatar
const PlayerInjuryRowMobile: React.FC<{ injury: PlayerInjury }> = ({ injury }) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ py: 1 }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: STATUS_COLORS[injury.status]?.dot || '#EF5350',
            flexShrink: 0,
          }}
        />
        <Avatar
          src={injury.avatar}
          alt={injury.playerName}
          sx={{ width: 32, height: 32, flexShrink: 0 }}
        />
        <Stack spacing={0} sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              sx={{
                fontFamily: '"Instrument Sans", sans-serif',
                fontSize: '13.33px',
                fontWeight: 600,
                color: '#F2F2F3',
                lineHeight: 1.5,
              }}
            >
              {injury.playerName}
            </Typography>
            {injury.projectedMinutes !== undefined && (
              <Typography
                sx={{
                  fontFamily: '"Instrument Sans", sans-serif',
                  fontSize: '11px',
                  fontWeight: 500,
                  color: 'rgba(242, 242, 243, 0.5)',
                  lineHeight: 1.5,
                }}
              >
                ({Math.round(injury.projectedMinutes)} min)
              </Typography>
            )}
            {injury.projectedMinutes === undefined && injury.detail && (
              <Typography
                sx={{
                  fontFamily: '"Instrument Sans", sans-serif',
                  fontSize: '11px',
                  fontWeight: 500,
                  color: 'rgba(242, 242, 243, 0.5)',
                  lineHeight: 1.5,
                }}
              >
                ({injury.detail})
              </Typography>
            )}
          </Stack>
          <Typography
            sx={{
              fontFamily: '"Instrument Sans", sans-serif',
              fontSize: '13.33px',
              fontWeight: 400,
              color: 'rgba(242, 242, 243, 0.38)',
              lineHeight: 1.5,
            }}
          >
            {injury.reason}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

const GameAccordion: React.FC<{ game: GameInjuryReport; isMobile: boolean; defaultExpanded?: boolean }> = ({ game, isMobile, defaultExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const awayInjuries = game.injuries.filter(i => i.team === game.awayTeam.abbr);
  const homeInjuries = game.injuries.filter(i => i.team === game.homeTeam.abbr);

  const PlayerRow = isMobile ? PlayerInjuryRowMobile : PlayerInjuryRowDesktop;

  return (
    <Box>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        disableGutters
        sx={{
          backgroundColor: '#212223',
          borderRadius: '8px !important',
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: '0 !important' },
          margin: '0 !important',
          boxShadow: '0px 1px 2px rgba(0,0,0,0.02)',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon size={24} color="rgba(242, 242, 243, 0.7)" />}
          sx={{
            px: 2,
            minHeight: 56,
            '&.Mui-expanded': { minHeight: 56 },
            '& .MuiAccordionSummary-content': {
              margin: '12px 0',
              '&.Mui-expanded': { margin: '12px 0' },
            },
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: '100%', pr: 2 }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                component="img"
                src={game.awayTeam.logo}
                alt={game.awayTeam.abbr}
                sx={{ width: 24, height: 24, borderRadius: '4px', objectFit: 'contain' }}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <Typography
                sx={{
                  fontFamily: '"Instrument Sans", sans-serif',
                  fontSize: '13.33px',
                  fontWeight: 600,
                  color: '#F2F2F3',
                }}
              >
                {isMobile ? game.awayTeam.abbr : game.awayTeam.name}
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Instrument Sans", sans-serif',
                  fontSize: '13.33px',
                  fontWeight: 400,
                  color: 'rgba(242, 242, 243, 0.7)',
                }}
              >
                @
              </Typography>
              <Box
                component="img"
                src={game.homeTeam.logo}
                alt={game.homeTeam.abbr}
                sx={{ width: 24, height: 24, borderRadius: '4px', objectFit: 'contain' }}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <Typography
                sx={{
                  fontFamily: '"Instrument Sans", sans-serif',
                  fontSize: '13.33px',
                  fontWeight: 600,
                  color: '#F2F2F3',
                }}
              >
                {isMobile ? game.homeTeam.abbr : game.homeTeam.name}
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Instrument Sans", sans-serif',
                  fontSize: '13.33px',
                  fontWeight: 400,
                  color: 'rgba(242, 242, 243, 0.7)',
                  ml: 1,
                }}
              >
                {game.gameTime}
              </Typography>
            </Stack>
            {game.impactLevel && (
              <Tooltip
                title={game.impactLevel === 'high'
                  ? 'A player projected 25+ minutes is listed Out, Doubtful, or Questionable'
                  : 'A player projected 15+ minutes is listed Out, Doubtful, or Questionable'}
                arrow
              >
                <Box>
                  <ImpactBadge level={game.impactLevel} />
                </Box>
              </Tooltip>
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            px: 2,
            pt: 1,
            pb: 2,
            backgroundColor: '#212223',
          }}
        >
          {/* Mobile: Stack teams vertically, Desktop: Side by side */}
          <Stack direction={isMobile ? 'column' : 'row'} spacing={isMobile ? 2 : 3}>
            {/* Away Team Injuries */}
            <Stack sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: '"Instrument Sans", sans-serif',
                  fontSize: '13.33px',
                  fontWeight: 600,
                  color: 'rgba(242, 242, 243, 0.7)',
                  mb: 1,
                }}
              >
                {game.awayTeam.name} Injuries
              </Typography>
              {awayInjuries.length > 0 ? (
                awayInjuries.map((injury, idx) => (
                  <PlayerRow key={`${injury.playerName}-${idx}`} injury={injury} />
                ))
              ) : (
                <Typography
                  sx={{
                    fontFamily: '"Instrument Sans", sans-serif',
                    fontSize: '13.33px',
                    color: 'rgba(242, 242, 243, 0.38)',
                    fontStyle: game.awayTeamSubmitted === false ? 'italic' : 'normal',
                  }}
                >
                  {game.awayTeamSubmitted === false ? 'Injury report not yet submitted' : 'No injuries reported'}
                </Typography>
              )}
            </Stack>

            {/* Divider - Vertical on desktop, Horizontal on mobile */}
            {!isMobile && (
              <Box
                sx={{
                  width: '1px',
                  alignSelf: 'stretch',
                  backgroundColor: 'rgba(255, 255, 255, 0.12)',
                  flexShrink: 0,
                }}
              />
            )}
            {isMobile && (
              <Box
                sx={{
                  height: '1px',
                  width: '100%',
                  backgroundColor: 'rgba(255, 255, 255, 0.12)',
                }}
              />
            )}

            {/* Home Team Injuries */}
            <Stack sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: '"Instrument Sans", sans-serif',
                  fontSize: '13.33px',
                  fontWeight: 600,
                  color: 'rgba(242, 242, 243, 0.7)',
                  mb: 1,
                }}
              >
                {game.homeTeam.name} Injuries
              </Typography>
              {homeInjuries.length > 0 ? (
                homeInjuries.map((injury, idx) => (
                  <PlayerRow key={`${injury.playerName}-${idx}`} injury={injury} />
                ))
              ) : (
                <Typography
                  sx={{
                    fontFamily: '"Instrument Sans", sans-serif',
                    fontSize: '13.33px',
                    color: 'rgba(242, 242, 243, 0.38)',
                    fontStyle: game.homeTeamSubmitted === false ? 'italic' : 'normal',
                  }}
                >
                  {game.homeTeamSubmitted === false ? 'Injury report not yet submitted' : 'No injuries reported'}
                </Typography>
              )}
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

// Shared content component for both mobile and desktop
const InjuryReportContent: React.FC<{
  data?: InjuryReportData;
  isLoading?: boolean;
  isMobile: boolean;
  defaultExpandedGameId?: string;
}> = ({ data, isLoading, isMobile, defaultExpandedGameId }) => (
  <Stack spacing="12px">
    {data?.games.map(game => (
      <GameAccordion
        key={game.gameId}
        game={game}
        isMobile={isMobile}
        defaultExpanded={game.gameId === defaultExpandedGameId}
      />
    ))}
    {(!data || data.games.length === 0) && (
      <Typography
        sx={{
          fontFamily: '"Instrument Sans", sans-serif',
          fontSize: '16px',
          color: 'rgba(242, 242, 243, 0.7)',
          textAlign: 'center',
          py: 4,
        }}
      >
        {isLoading ? 'Loading injury report...' : 'No injury data available'}
      </Typography>
    )}
  </Stack>
);

export const InjuryReportModal: React.FC<InjuryReportModalProps> = ({
  open,
  onClose,
  data,
  isLoading,
  defaultExpandedGameId,
  league = 'NBA',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const title = `${league.toUpperCase()} Injury Report`;

  // Calculate status counts
  const statusCounts = {
    Out: 0,
    Doubtful: 0,
    Questionable: 0,
    Probable: 0,
  };

  data?.games.forEach(game => {
    game.injuries.forEach(injury => {
      if (statusCounts[injury.status] !== undefined) {
        statusCounts[injury.status]++;
      }
    });
  });

  // Mobile: Bottom sheet (Drawer)
  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            backgroundColor: '#343536',
            borderRadius: '16px 16px 0 0',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            maxHeight: '90vh',
            boxShadow: '0px -5px 10px rgba(0,0,0,0.22)',
          },
        }}
      >
        <Stack sx={{ height: '100%', overflow: 'hidden' }}>
          {/* Grabber and close button header */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr',
              alignItems: 'center',
              pt: 1,
              px: 2,
            }}
          >
            <Box /> {/* Empty left cell */}
            <Box
              sx={{
                width: 40,
                height: 4,
                backgroundColor: 'rgba(255, 255, 255, 0.16)',
                borderRadius: '8px',
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton
                onClick={onClose}
                sx={{
                  color: 'rgba(242, 242, 243, 0.7)',
                  p: 1,
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Header */}
          <Box sx={{ px: 2, pb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Stack spacing={0.5}>
                <Typography
                  sx={{
                    fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
                    fontWeight: 900,
                    fontSize: '19.2px',
                    color: '#F2F2F3',
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"Instrument Sans", sans-serif',
                    fontWeight: 600,
                    fontSize: '13.33px',
                    color: 'rgba(242, 242, 243, 0.7)',
                  }}
                >
                  {data?.games.length || 0} games on today's slate
                </Typography>
              </Stack>
              <Stack alignItems="flex-end" spacing={0.5}>
                <Typography
                  sx={{
                    fontFamily: '"Instrument Sans", sans-serif',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#F2F2F3',
                    lineHeight: 1.5,
                  }}
                >
                  {data?.date || 'Loading...'}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"Instrument Sans", sans-serif',
                    fontSize: '13.33px',
                    fontWeight: 400,
                    color: 'rgba(242, 242, 243, 0.7)',
                    lineHeight: 1.5,
                  }}
                >
                  Last updated: {data?.lastUpdated || '--'}
                </Typography>
              </Stack>
            </Stack>

            {/* Status Filter Chips - wrap on mobile */}
            <Box sx={{ mt: 2 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <StatusFilterChip status="Out" count={statusCounts.Out} dotColor="#EF5350" />
                <StatusFilterChip status="Doubtful" count={statusCounts.Doubtful} dotColor="#E57373" />
                <StatusFilterChip status="Questionable" count={statusCounts.Questionable} dotColor="#FFA726" />
                <StatusFilterChip status="Probable" count={statusCounts.Probable} dotColor="#66BB6A" />
              </Stack>
            </Box>
          </Box>

          {/* Divider */}
          <Box sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.12)' }} />

          {/* Content - scrollable */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              px: 2,
              py: 2,
            }}
          >
            <InjuryReportContent
              data={data}
              isLoading={isLoading}
              isMobile={isMobile}
              defaultExpandedGameId={defaultExpandedGameId}
            />
          </Box>
        </Stack>
      </Drawer>
    );
  }

  // Desktop: Dialog
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#2A2B2C',
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Stack spacing={0.5}>
          <Typography
            variant="h4"
            sx={{
              fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
              fontWeight: 900,
              color: '#F2F2F3',
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Instrument Sans", sans-serif',
              fontWeight: 600,
              fontSize: '16px',
              color: 'rgba(242, 242, 243, 0.7)',
            }}
          >
            {data?.games.length || 0} games on today's slate
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Stack alignItems="flex-end" spacing={0}>
            <Typography
              sx={{
                fontFamily: '"Instrument Sans", sans-serif',
                fontSize: '16px',
                fontWeight: 600,
                color: '#F2F2F3',
                lineHeight: 1.5,
              }}
            >
              {data?.date || 'Loading...'}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Instrument Sans", sans-serif',
                fontSize: '13.33px',
                fontWeight: 400,
                color: 'rgba(242, 242, 243, 0.7)',
                lineHeight: 1.5,
              }}
            >
              Last updated: {data?.lastUpdated || '--'}
            </Typography>
          </Stack>
          <IconButton
            onClick={onClose}
            sx={{
              color: 'rgba(242, 242, 243, 0.7)',
              p: 1,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      {/* Status Filter Chips */}
      <Box sx={{ px: 3, pb: 2 }}>
        <Stack direction="row" spacing={1}>
          <StatusFilterChip status="Out" count={statusCounts.Out} dotColor="#EF5350" />
          <StatusFilterChip status="Doubtful" count={statusCounts.Doubtful} dotColor="#E57373" />
          <StatusFilterChip status="Questionable" count={statusCounts.Questionable} dotColor="#FFA726" />
          <StatusFilterChip status="Probable" count={statusCounts.Probable} dotColor="#66BB6A" />
        </Stack>
      </Box>

      <DialogContent
        sx={{
          px: 3,
          py: 2,
          backgroundColor: '#242526',
          borderTop: '1px solid rgba(255, 255, 255, 0.12)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
        }}
      >
        <InjuryReportContent
          data={data}
          isLoading={isLoading}
          isMobile={isMobile}
          defaultExpandedGameId={defaultExpandedGameId}
        />
      </DialogContent>
    </Dialog>
  );
};

export default InjuryReportModal;
